import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { InfoTip } from "../components/InfoTip";
import { fmtNum } from "../lib/format";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  CartesianGrid, BarChart, Bar, Cell, ReferenceLine,
} from "recharts";

const P = "hsl(var(--primary))";
const PROFIT = "hsl(var(--profit))";
const LOSS = "hsl(var(--loss))";
const GRID = "hsl(var(--border))";
const MUTED = "hsl(var(--muted-foreground))";

function Kpi({ label, value, cls = "", testid, tip }) {
  return (
    <div className="border border-border bg-card px-4 py-3" data-testid={testid}>
      <div className="label-caps text-muted-foreground flex items-center gap-1.5">
        {label}
        {tip && <InfoTip text={tip} />}
      </div>
      <div className={`font-mono text-xl mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-muted-foreground p-8">Loading…</div>;

  if (!data.closed_trades) {
    return (
      <div className="space-y-6" data-testid="analytics-page">
        <h1 className="font-heading font-bold text-3xl tracking-tight">Analytics</h1>
        <div className="border border-border bg-card p-12 text-center text-muted-foreground" data-testid="analytics-empty">
          No closed trades yet. Analytics appear once you close trades with a P/L.
        </div>
      </div>
    );
  }

  const fmt = (n) => fmtNum(n, 4);
  const signed = (n) => (n == null ? "—" : `${n >= 0 ? "+" : ""}${fmt(n)}`);
  const pnlCls = (n) => (n == null ? "" : n >= 0 ? "text-profit" : "text-loss");

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <h1 className="font-heading font-bold text-3xl tracking-tight">Analytics</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi testid="an-total-pnl" label="Total P/L" value={signed(data.total_pnl)} cls={pnlCls(data.total_pnl)}
          tip="Sum of the realized profit/loss of all closed trades." />
        <Kpi testid="an-win-rate" label="Win Rate" value={`${data.win_rate}%`}
          tip="Percentage of closed trades that ended with a positive P/L." />
        <Kpi testid="an-profit-factor" label="Profit Factor" value={data.profit_factor ?? "—"}
          tip="Gross profit divided by gross loss. Above 1 means you win more money than you lose overall; 2+ is strong." />
        <Kpi testid="an-expectancy" label="Expectancy / Trade" value={signed(data.expectancy)} cls={pnlCls(data.expectancy)}
          tip="Average P/L per closed trade — what one trade earns (or loses) you on average." />
        <Kpi testid="an-avg-win" label="Avg Win" value={signed(data.avg_win)} cls="text-profit"
          tip="Average profit of your winning trades only." />
        <Kpi testid="an-avg-loss" label="Avg Loss" value={signed(data.avg_loss)} cls="text-loss"
          tip="Average loss of your losing trades only." />
        <Kpi testid="an-avg-r" label="Avg R-Multiple" value={data.avg_r != null ? `${data.avg_r}R` : "—"} cls={pnlCls(data.avg_r)}
          tip="Average profit measured in units of initial risk (R = P/L ÷ (distance entry→stop × quantity)). +2R means you earned twice what you risked. Only trades with a stop loss count." />
        <Kpi testid="an-best-worst" label="Best / Worst"
          value={`${signed(data.best_trade.pnl)} / ${signed(data.worst_trade.pnl)}`}
          tip="P/L of your single best and single worst closed trade." />
      </div>

      <div className="border border-border bg-card" data-testid="equity-curve-chart">
        <div className="px-5 py-3 border-b border-border label-caps text-muted-foreground">Equity Curve (cumulative P/L)</div>
        <div className="p-4 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.equity_curve}>
              <defs>
                <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={P} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={P} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
              <XAxis dataKey="n" tick={{ fill: MUTED, fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <YAxis tick={{ fill: MUTED, fontSize: 11, fontFamily: "JetBrains Mono" }} width={70} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--popover))", border: `1px solid ${GRID}`, fontFamily: "JetBrains Mono", fontSize: 12 }}
                labelFormatter={(n) => {
                  const pt = data.equity_curve[n - 1];
                  return pt ? `#${n} · ${pt.symbol} · ${pt.date}` : `#${n}`;
                }}
              />
              <ReferenceLine y={0} stroke={MUTED} strokeDasharray="4 4" />
              <Area type="monotone" dataKey="equity" stroke={P} strokeWidth={2} fill="url(#eq)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {data.by_tag?.length > 0 && (
        <div className="border border-border bg-card" data-testid="performance-by-setup">
          <div className="px-5 py-3 border-b border-border label-caps text-muted-foreground flex items-center gap-1.5">
            Performance by Setup
            <InfoTip text="Stats grouped by the setup tags on your trades. Trades with multiple tags count toward each tag; trades without tags appear as (untagged)." />
          </div>
          <div className="p-4 overflow-x-auto">
            <table className="w-full text-sm font-mono">
              <thead>
                <tr className="text-muted-foreground text-xs">
                  <th className="text-left font-normal pb-1">Setup</th>
                  <th className="text-right font-normal pb-1">Trades</th>
                  <th className="text-right font-normal pb-1">Win %</th>
                  <th className="text-right font-normal pb-1">Avg R</th>
                  <th className="text-right font-normal pb-1">Avg P/L</th>
                  <th className="text-right font-normal pb-1">Total P/L</th>
                </tr>
              </thead>
              <tbody>
                {data.by_tag.map((r) => (
                  <tr key={r.key} className="border-t border-border" data-testid={`setup-row-${r.key}`}>
                    <td className="py-1.5 uppercase">{r.key}</td>
                    <td className="text-right">{r.trades}</td>
                    <td className="text-right">{r.win_rate}%</td>
                    <td className={`text-right ${pnlCls(r.avg_r)}`}>{r.avg_r != null ? `${r.avg_r}R` : "—"}</td>
                    <td className={`text-right ${pnlCls(r.avg_pnl)}`}>{signed(r.avg_pnl)}</td>
                    <td className={`text-right ${pnlCls(r.pnl)}`}>{signed(r.pnl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-border bg-card" data-testid="pnl-by-symbol-chart">
          <div className="px-5 py-3 border-b border-border label-caps text-muted-foreground">P/L by Symbol</div>
          <div className="p-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_symbol}>
                <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
                <XAxis dataKey="key" tick={{ fill: MUTED, fontSize: 11, fontFamily: "JetBrains Mono" }} />
                <YAxis tick={{ fill: MUTED, fontSize: 11, fontFamily: "JetBrains Mono" }} width={70} />
                <Tooltip cursor={{ fill: "hsl(var(--accent))" }}
                  contentStyle={{ background: "hsl(var(--popover))", border: `1px solid ${GRID}`, fontFamily: "JetBrains Mono", fontSize: 12 }} />
                <ReferenceLine y={0} stroke={MUTED} />
                <Bar dataKey="pnl">
                  {data.by_symbol.map((s) => (
                    <Cell key={s.key} fill={s.pnl >= 0 ? PROFIT : LOSS} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-border bg-card" data-testid="breakdown-tables">
          <div className="px-5 py-3 border-b border-border label-caps text-muted-foreground">Breakdown</div>
          <div className="p-4 space-y-5">
            {[["By Symbol", data.by_symbol], ["By Side", data.by_side]].map(([title, rows]) => (
              <div key={title}>
                <div className="label-caps text-muted-foreground mb-2">{title}</div>
                <table className="w-full text-sm font-mono">
                  <thead>
                    <tr className="text-muted-foreground text-xs">
                      <th className="text-left font-normal pb-1">Key</th>
                      <th className="text-right font-normal pb-1">Trades</th>
                      <th className="text-right font-normal pb-1">Win %</th>
                      <th className="text-right font-normal pb-1">P/L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.key} className="border-t border-border" data-testid={`breakdown-${title === "By Side" ? "side" : "symbol"}-${r.key}`}>
                        <td className="py-1.5 uppercase">{r.key}</td>
                        <td className="text-right">{r.trades}</td>
                        <td className="text-right">{r.win_rate}%</td>
                        <td className={`text-right ${pnlCls(r.pnl)}`}>{signed(r.pnl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
